import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from 'react';
import { prepareProps } from '../../utils/propUtils';
import { classes } from '../../utils/utils';
import { ModalWithBackdrop, ModalWithBackdropProps } from '../ModalWithBackdrop/ModalWithBackdrop';
import styles from './BaseDialog.module.css';
import { BaseDialogTabTitle } from './BaseDialogTabTitle/BaseDialogTabTitle';
export type BaseDialogProps<TAnyTabName extends string> = {
  readonly opened: boolean;
  readonly activeTabName: TAnyTabName;
  /**
   * Makes the content container as high as possible (there will still be space between the screen edges and the dialog).
   * Also puts the content into a flex column layout
   */
  readonly fullHeight?: boolean;
  readonly renderButtons: () => Array<React.ReactNode>;
  readonly onTabSelected?: (name: TAnyTabName) => void;
  readonly onBackdropClick?: ModalWithBackdropProps['onBackdropClick'];
  readonly children: () => { [key in TAnyTabName]: BaseDialogTab<key> };
  readonly 'data-testid'?: string;
};
export type BaseDialogTab<TName extends string> = {
  name: TName;
  title: string;
  renderContent: () => React.ReactNode;
};

// TODO:
export const BaseDialog = React.memo(props => {
  const $ = _c(32);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    opened,
    renderButtons,
    onBackdropClick,
    onTabSelected,
    activeTabName,
    "data-testid": dataTestId,
    children,
    fullHeight: t1
  } = t0;
  const fullHeight = t1 === undefined ? false : t1;
  let t2;
  if ($[2] !== children) {
    t2 = children();
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const tabConfiguration = t2;
  let t3;
  if ($[4] !== tabConfiguration) {
    t3 = Object.values(tabConfiguration);
    $[4] = tabConfiguration;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const tabs = t3;
  let t4;
  if ($[6] !== activeTabName || $[7] !== onTabSelected || $[8] !== tabs) {
    t4 = tabs.map(tab => <BaseDialogTabTitle name={tab.name} title={tab.title} selected={tab.name === activeTabName} key={tab.name} onSelected={onTabSelected} />);
    $[6] = activeTabName;
    $[7] = onTabSelected;
    $[8] = tabs;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const renderedTabTitles = t4;
  const t5 = tabConfiguration[activeTabName];
  let t6;
  if ($[10] !== t5) {
    t6 = t5.renderContent();
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const renderedTabContent = t6;
  let t7;
  if ($[12] !== renderButtons) {
    t7 = renderButtons();
    $[12] = renderButtons;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const renderedButtons = t7;
  let t8;
  if ($[14] !== renderedTabTitles) {
    t8 = <div className={styles.tabTitles} data-testid="BaseDialog.TabTitles">{renderedTabTitles}</div>;
    $[14] = renderedTabTitles;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== renderedButtons) {
    t9 = <div>{renderedButtons}</div>;
    $[16] = renderedButtons;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <div className={styles.headerBar}>{t8}{t9}</div>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = fullHeight ? styles.fullHeightContent : null;
  let t12;
  if ($[21] !== t11) {
    t12 = classes(styles.content, t11);
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== renderedTabContent || $[24] !== t12) {
    t13 = <div className={t12}>{renderedTabContent}</div>;
    $[23] = renderedTabContent;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== dataTestId || $[27] !== onBackdropClick || $[28] !== opened || $[29] !== t10 || $[30] !== t13) {
    t14 = <ModalWithBackdrop opened={opened} onBackdropClick={onBackdropClick} data-testid={dataTestId}>{t10}{t13}</ModalWithBackdrop>;
    $[26] = dataTestId;
    $[27] = onBackdropClick;
    $[28] = opened;
    $[29] = t10;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}) as <TAnyTabName extends string>(props: BaseDialogProps<TAnyTabName>) => React.JSX.Element;